import { useEffect, useMemo } from 'react';
import { ChargeDto, PayOrderStatus } from '@tiendanube/common';
import { useNavegate } from 'App/hooks';
import { combineStatus } from 'commons/utils/combineStatus';
import { useGetIsFreePlan, useStoreStatus } from 'domains/Auth/hooks';
import { useGetLastPayment, useGetSubscriptions } from '../Checkout/hooks';
import { ChurnStatus, trackingChurnStatusCheck } from '../tracking';

function getChurnedStatus(
  isStoreDown?: boolean,
  isFreePlan?: boolean,
  hasActivePlanCharge?: boolean,
  hasPlanSubscription?: boolean,
): ChurnStatus | undefined {
  if (
    [
      isStoreDown,
      isFreePlan,
      hasActivePlanCharge,
      hasPlanSubscription,
    ].includes(undefined)
  )
    return undefined;
  if (!isStoreDown) return 'store_active';
  if (isFreePlan) return 'has_free_plan';
  if (!hasActivePlanCharge) return 'needs_payment';
  return hasPlanSubscription ? 'unsynced' : 'has_remaining_days';
}

function getActivePlanCharge(
  lastRelevantCharge: ChargeDto | undefined,
  paymentStatus: PayOrderStatus | undefined,
) {
  return (
    !!lastRelevantCharge &&
    paymentStatus === 'PAID' &&
    new Date(lastRelevantCharge.toDate) > new Date()
  );
}

export default function useStoreChurnedStatus() {
  const { storeStatus } = useStoreStatus();
  const { isFreePlan, isTrial } = useGetIsFreePlan();
  const isStoreDown = storeStatus === 'store_down';

  const { pathname } = useNavegate();

  const {
    status: planChargeStatus,
    lastRelevantCharge: lastRelevantChargePlan,
    paymentStatus: paymentStatusPlan,
  } = useGetLastPayment('plan-cost', undefined, isStoreDown || isTrial);
  const {
    status: planUpgradeChargeStatus,
    lastRelevantCharge: lastRelevantChargePlanUpgrade,
    paymentStatus: paymentStatusPlanUpgrade,
  } = useGetLastPayment('plan-cost-upgrade', undefined, isStoreDown || isTrial);
  const { status: subStatus, subscriptionForPlan } = useGetSubscriptions(
    isStoreDown || isTrial,
  );

  const statusObject = combineStatus(
    planChargeStatus,
    planUpgradeChargeStatus,
    subStatus,
  );

  const hasActivePlanCharge = useMemo(
    () =>
      statusObject.isSuccess
        ? getActivePlanCharge(lastRelevantChargePlan, paymentStatusPlan) ||
          getActivePlanCharge(
            lastRelevantChargePlanUpgrade,
            paymentStatusPlanUpgrade,
          )
        : undefined,
    [
      statusObject,
      lastRelevantChargePlan,
      lastRelevantChargePlanUpgrade,
      paymentStatusPlan,
      paymentStatusPlanUpgrade,
    ],
  );

  const hasPlanSubscription = !!subscriptionForPlan;

  const canReactivateWithoutPayment =
    isStoreDown &&
    (isFreePlan || (hasPlanSubscription === false && hasActivePlanCharge));

  const churnStatus = useMemo(
    () =>
      getChurnedStatus(
        isStoreDown,
        isFreePlan,
        hasActivePlanCharge,
        !!subscriptionForPlan,
      ),
    [isStoreDown, isFreePlan, hasActivePlanCharge, subscriptionForPlan],
  );

  useEffect(() => {
    if (churnStatus && isStoreDown) {
      trackingChurnStatusCheck({
        status: churnStatus,
        route: pathname,
      });
    }
  }, [isStoreDown, churnStatus, pathname]);
  return {
    hasActivePlanCharge,
    ...statusObject,
    isStoreDown,
    canReactivateWithoutPayment,
  };
}
